import 'chart.js/auto';

import { useTheme } from '@brunommsilva/react-foundations';
import { ScriptableContext } from 'chart.js';
import { Chart } from 'react-chartjs-2';

interface Serie {
  label: string;
  values: number[];
  colors?: string[];
  type?: 'bar' | 'line';
}

interface Props {
  title?: string;
  labels: string[];
  series: Serie[];
}

const BarChart = ({ labels, series }: Props) => {
  const { theme } = useTheme();

  return (
    <Chart
      type="line"
      options={{
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: theme.palette.text.secondary,
              maxTicksLimit: 12,
            },
          },
          y: {
            grid: {
              color: (context: { tick: { value: number } }) =>
                context.tick.value === 0
                  ? theme.palette.text.secondary
                  : theme.palette.divider,
              lineWidth: (context: { tick: { value: number } }) =>
                context.tick.value === 0 ? 1.5 : 1,
            },
            ticks: {
              color: theme.palette.text.secondary,
              maxTicksLimit: 6,
            },
          },
        },
        plugins: {
          legend: {
            labels: {
              color: theme.palette.text.primary,
              boxWidth: 8,
              boxHeight: 8,
              usePointStyle: true,
              pointStyle: 'circle',
            },
          },
        },
      }}
      data={{
        labels,
        datasets: series.map(({ label, values, colors, type }) => {
          const isLine = type === 'line';
          const baseColor =
            colors && colors[0] ? colors[0] : theme.palette.primary.main;
          if (isLine) {
            return {
              type: 'line' as const,
              label,
              data: values,
              backgroundColor: 'transparent',
              borderColor: baseColor,
              borderWidth: 3,
              tension: 0.35,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointHitRadius: 15,
              pointBackgroundColor: baseColor,
              pointBorderColor: theme.palette.background.paper,
              pointBorderWidth: 2,
            };
          }
          return {
            type: 'bar' as const,
            label,
            data: values,
            backgroundColor: (context: ScriptableContext<'bar'>) => {
              const chart = context.chart;
              const { ctx, chartArea } = chart;
              if (!chartArea) {
                return baseColor;
              }
              const gradient = ctx.createLinearGradient(
                0,
                chartArea.top,
                0,
                chartArea.bottom
              );
              gradient.addColorStop(0, baseColor); // Full color at the top
              gradient.addColorStop(1, `${baseColor}33`); // ~20% opacity at the bottom
              return gradient;
            },
            borderColor: baseColor,
            borderWidth: 0,
            borderRadius: 6,
            borderSkipped: false,
            barPercentage: 0.8,
            categoryPercentage: 0.7,
          };
        }),
      }}
    />
  );
};

export default BarChart;
